/**
 * Hook to fetch team members with access to a specific project
 * Used for assignee dropdown in service projects
 * Feature flag: ENABLE_SERVICE_ITEM_ASSIGNMENT
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { chunkedInQuerySimple } from '@/lib/queries/chunkedInQuery';

export interface TeamMember {
  id: string;
  full_name: string;
  avatar_url?: string | null;
  email?: string;
}

export const useProjectTeamMembers = (projectId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['project-team-members', projectId],
    queryFn: async (): Promise<TeamMember[]> => {
      logger.debug('[useProjectTeamMembers] Fetching team members for project:', projectId);
      
      // Step 1: Get user_ids from project_user_permissions (separate query to avoid join issues)
      const { data: permissions, error: permError } = await supabase
        .from('project_user_permissions')
        .select('user_id')
        .eq('project_id', projectId)
        .eq('can_view', true);

      if (permError) {
        logger.error('[useProjectTeamMembers] Error fetching permissions:', permError);
        throw permError;
      }

      const userIds = permissions?.map(p => p.user_id).filter(Boolean) || [];
      if (userIds.length === 0) {
        logger.debug('[useProjectTeamMembers] No team members found');
        return [];
      }

      // Step 2: Get profiles for those user_ids (using chunked query)
      const profiles = await chunkedInQuerySimple<{ id: string; full_name: string | null; avatar_url: string | null; email: string | null }>(
        supabase,
        'profiles' as any,
        'id',
        userIds,
        'id, full_name, avatar_url, email'
      );

      // Transform to TeamMember structure
      const members: TeamMember[] = (profiles || [])
        .map((profile) => ({
          id: profile.id,
          full_name: profile.full_name || profile.email || 'Unknown',
          avatar_url: profile.avatar_url,
          email: profile.email || undefined,
        }))
        // Remove duplicates
        .filter((member, index, self) => 
          index === self.findIndex(m => m.id === member.id)
        )
        // Sort by name
        .sort((a, b) => a.full_name.localeCompare(b.full_name));

      logger.debug('[useProjectTeamMembers] Fetched team members:', members.length);
      return members;
    },
    enabled: !!projectId && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false,           // ✅ Prevent reload on back navigation
    refetchOnWindowFocus: false,     // ✅ Match AUTH_QUERY_CONFIG
    placeholderData: keepPreviousData, // ✅ Smooth UX when data changes
  });
};
